import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Select,
  Badge,
  Spinner,
  useToast,
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  HStack,
  IconButton,
  VStack,
  Progress,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
} from "@chakra-ui/react";
import { FiRefreshCw } from "react-icons/fi";
import { dashboardService, projectService } from "../services/edosService";

const HEALTH_COLOR = {
  green: "green",
  amber: "orange",
  red: "red",
  unknown: "gray",
};

function StatCard({ label, value, helpText, color = "blue" }) {
  const bg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.600");
  return (
    <Box bg={bg} border="1px" borderColor={border} borderRadius="md" p={4}>
      <Stat>
        <StatLabel color="gray.500">{label}</StatLabel>
        <StatNumber color={`${color}.500`}>{value ?? 0}</StatNumber>
        {helpText && <StatHelpText>{helpText}</StatHelpText>}
      </Stat>
    </Box>
  );
}

export default function EDOSDashboardsPage() {
  const toast = useToast();
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [view, setView] = useState("executive");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  const fetchProjects = useCallback(async () => {
    try {
      const res = await projectService.list({ status: "active" });
      setProjects(res.data.data || res.data.projects || []);
    } catch (_) {}
  }, []);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setData(null);
    try {
      let res;
      if (view === "executive") res = await dashboardService.executive();
      else if (view === "project-manager")
        res = await dashboardService.projectManager();
      else if (view === "developer") res = await dashboardService.developer();
      else if (view === "sprint-board" && selectedProjectId)
        res = await dashboardService.sprintBoard(selectedProjectId);
      else if (view === "release-pipeline" && selectedProjectId)
        res = await dashboardService.releasePipeline(selectedProjectId);
      else {
        setLoading(false);
        return;
      }
      setData(res.data.data || res.data);
    } catch (err) {
      toast({ title: "Failed to load dashboard", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [view, selectedProjectId, toast]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const needsProject = view === "sprint-board" || view === "release-pipeline";

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            EDOS Dashboards
          </Text>
          <Text color="gray.500" fontSize="sm">
            Role-based delivery intelligence
          </Text>
        </Box>
        <HStack>
          <IconButton
            icon={<FiRefreshCw />}
            variant="outline"
            aria-label="Refresh"
            onClick={fetchDashboard}
          />
        </HStack>
      </Flex>

      <Tabs
        variant="enclosed"
        onChange={(i) => {
          const views = [
            "executive",
            "project-manager",
            "developer",
            "sprint-board",
            "release-pipeline",
          ];
          setView(views[i]);
          setData(null);
        }}
      >
        <TabList mb={4}>
          <Tab>Executive</Tab>
          <Tab>Project Manager</Tab>
          <Tab>Developer</Tab>
          <Tab>Sprint Board</Tab>
          <Tab>Release Pipeline</Tab>
        </TabList>

        {needsProject && (
          <Flex mb={4} align="center" gap={3}>
            <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
              Project:
            </Text>
            <Select
              placeholder="Select a project..."
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              maxW="300px"
            >
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.projectName}
                </option>
              ))}
            </Select>
          </Flex>
        )}

        <TabPanels>
          {/* Executive */}
          <TabPanel px={0}>
            <DashboardContent
              loading={loading}
              data={data}
              type="executive"
              bg={bg}
              borderColor={borderColor}
              headerBg={headerBg}
            />
          </TabPanel>

          {/* Project Manager */}
          <TabPanel px={0}>
            <DashboardContent
              loading={loading}
              data={data}
              type="project-manager"
              bg={bg}
              borderColor={borderColor}
              headerBg={headerBg}
            />
          </TabPanel>

          {/* Developer */}
          <TabPanel px={0}>
            <DashboardContent
              loading={loading}
              data={data}
              type="developer"
              bg={bg}
              borderColor={borderColor}
              headerBg={headerBg}
            />
          </TabPanel>

          {/* Sprint Board */}
          <TabPanel px={0}>
            {!selectedProjectId ? (
              <Alert status="info">
                <AlertIcon />
                Select a project to view its sprint board.
              </Alert>
            ) : (
              <DashboardContent
                loading={loading}
                data={data}
                type="sprint-board"
                bg={bg}
                borderColor={borderColor}
                headerBg={headerBg}
              />
            )}
          </TabPanel>

          {/* Release Pipeline */}
          <TabPanel px={0}>
            {!selectedProjectId ? (
              <Alert status="info">
                <AlertIcon />
                Select a project to view its release pipeline.
              </Alert>
            ) : (
              <DashboardContent
                loading={loading}
                data={data}
                type="release-pipeline"
                bg={bg}
                borderColor={borderColor}
                headerBg={headerBg}
              />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}

function DashboardContent({ loading, data, type, bg, borderColor, headerBg }) {
  if (loading) {
    return (
      <Flex justify="center" p={10}>
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }
  if (!data) {
    return (
      <Alert status="info">
        <AlertIcon />
        No data available. Try refreshing.
      </Alert>
    );
  }

  // Render executive / project-manager / developer as generic key-value grid
  if (["executive", "project-manager", "developer"].includes(type)) {
    return (
      <GenericDashboard
        data={data}
        bg={bg}
        borderColor={borderColor}
        headerBg={headerBg}
      />
    );
  }

  if (type === "sprint-board") {
    return (
      <SprintBoardView
        data={data}
        bg={bg}
        borderColor={borderColor}
        headerBg={headerBg}
      />
    );
  }

  if (type === "release-pipeline") {
    return (
      <ReleasePipelineView
        data={data}
        bg={bg}
        borderColor={borderColor}
        headerBg={headerBg}
      />
    );
  }

  return null;
}

function GenericDashboard({ data, bg, borderColor, headerBg }) {
  const flatEntries = Object.entries(data).filter(
    ([, v]) => typeof v !== "object" || v === null,
  );
  const objectEntries = Object.entries(data).filter(
    ([, v]) => typeof v === "object" && v !== null && !Array.isArray(v),
  );
  const arrayEntries = Object.entries(data).filter(([, v]) => Array.isArray(v));

  return (
    <VStack spacing={4} align="stretch">
      {flatEntries.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          {flatEntries.map(([k, v]) => (
            <Box
              key={k}
              bg={bg}
              border="1px"
              borderColor={borderColor}
              borderRadius="md"
              p={4}
            >
              <Stat>
                <StatLabel color="gray.500" textTransform="capitalize">
                  {k.replace(/([A-Z])/g, " $1")}
                </StatLabel>
                <StatNumber fontSize="xl">{String(v ?? 0)}</StatNumber>
              </Stat>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {objectEntries.map(([k, v]) => (
        <Box
          key={k}
          bg={bg}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
          p={4}
        >
          <Text fontWeight="bold" mb={3} textTransform="capitalize">
            {k.replace(/([A-Z])/g, " $1")}
          </Text>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
            {Object.entries(v).map(([subK, subV]) => (
              <Box key={subK} p={3} bg={headerBg} borderRadius="md">
                <Text fontSize="xs" color="gray.500" textTransform="capitalize">
                  {subK.replace(/([A-Z])/g, " $1")}
                </Text>
                <Text fontWeight="semibold">{String(subV ?? "-")}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      ))}

      {arrayEntries.map(([k, arr]) => (
        <Box
          key={k}
          bg={bg}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
          p={4}
        >
          <Text fontWeight="bold" mb={3} textTransform="capitalize">
            {k.replace(/([A-Z])/g, " $1")}
          </Text>
          {arr.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              No items
            </Text>
          ) : (
            <Table variant="simple" size="sm">
              <Thead bg={headerBg}>
                <Tr>
                  {Object.keys(arr[0] || {})
                    .slice(0, 6)
                    .map((col) => (
                      <Th key={col}>{col}</Th>
                    ))}
                </Tr>
              </Thead>
              <Tbody>
                {arr.slice(0, 10).map((row, i) => (
                  <Tr key={i}>
                    {Object.values(row)
                      .slice(0, 6)
                      .map((cell, j) => (
                        <Td key={j} fontSize="sm">
                          {typeof cell === "object"
                            ? JSON.stringify(cell)
                            : String(cell ?? "-")}
                        </Td>
                      ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      ))}
    </VStack>
  );
}

function SprintBoardView({ data, bg, borderColor, headerBg }) {
  const sprint = data.activeSprint || data.sprint || data;
  const tickets = data.tickets || data.board || [];
  const columns = ["open", "in_progress", "in_review", "done"];

  return (
    <VStack spacing={4} align="stretch">
      {sprint?.sprintName && (
        <Box
          bg={bg}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
          p={4}
        >
          <Flex justify="space-between" align="center">
            <Box>
              <Text fontWeight="bold" fontSize="lg">
                {sprint.sprintName}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {sprint.sprintGoal}
              </Text>
            </Box>
            <Badge
              colorScheme={sprint.status === "active" ? "green" : "gray"}
              fontSize="sm"
              px={2}
              py={1}
            >
              {sprint.status}
            </Badge>
          </Flex>
          {sprint.plannedStoryPoints && (
            <Box mt={3}>
              <Flex justify="space-between" mb={1}>
                <Text fontSize="xs">Story Points Progress</Text>
                <Text fontSize="xs">
                  {sprint.completedStoryPoints || 0} /{" "}
                  {sprint.plannedStoryPoints}
                </Text>
              </Flex>
              <Progress
                value={
                  ((sprint.completedStoryPoints || 0) /
                    sprint.plannedStoryPoints) *
                  100
                }
                colorScheme="green"
                borderRadius="full"
              />
            </Box>
          )}
        </Box>
      )}

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        {columns.map((col) => {
          const colTickets = Array.isArray(tickets)
            ? tickets.filter((t) => t.workflowState === col || t.status === col)
            : [];
          return (
            <Box
              key={col}
              bg={bg}
              border="1px"
              borderColor={borderColor}
              borderRadius="md"
              p={3}
            >
              <Flex justify="space-between" mb={2}>
                <Text
                  fontWeight="semibold"
                  fontSize="sm"
                  textTransform="capitalize"
                >
                  {col.replace(/_/g, " ")}
                </Text>
                <Badge>{colTickets.length}</Badge>
              </Flex>
              <VStack align="stretch" spacing={2}>
                {colTickets.slice(0, 5).map((t) => (
                  <Box
                    key={t._id}
                    p={2}
                    bg={headerBg}
                    borderRadius="md"
                    borderLeft="3px solid"
                    borderLeftColor="blue.400"
                  >
                    <Text fontSize="xs" fontWeight="semibold" noOfLines={2}>
                      {t.title}
                    </Text>
                    {t.storyPoints && (
                      <Text fontSize="xs" color="gray.500">
                        {t.storyPoints} pts
                      </Text>
                    )}
                  </Box>
                ))}
                {colTickets.length === 0 && (
                  <Text fontSize="xs" color="gray.400">
                    No tickets
                  </Text>
                )}
              </VStack>
            </Box>
          );
        })}
      </SimpleGrid>
    </VStack>
  );
}

function ReleasePipelineView({ data, bg, borderColor, headerBg }) {
  const releases = data.releases || (Array.isArray(data) ? data : []);

  const ENV_COLORS = {
    dev: "blue",
    qa: "orange",
    uat: "purple",
    staging: "yellow",
    production: "green",
  };

  return (
    <VStack spacing={4} align="stretch">
      {releases.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          No releases in pipeline.
        </Alert>
      ) : (
        releases.map((r) => (
          <Box
            key={r._id}
            bg={bg}
            border="1px"
            borderColor={borderColor}
            borderRadius="md"
            p={4}
          >
            <Flex justify="space-between" align="center" mb={2}>
              <Box>
                <Text fontWeight="bold">{r.releaseName}</Text>
                <Text fontSize="xs" color="gray.500">
                  {r.releaseCode}
                </Text>
              </Box>
              <HStack>
                <Badge colorScheme={ENV_COLORS[r.environment] || "gray"}>
                  {r.environment}
                </Badge>
                <Badge
                  colorScheme={
                    r.deploymentStatus === "deployed"
                      ? "green"
                      : r.deploymentStatus === "failed"
                        ? "red"
                        : "gray"
                  }
                >
                  {r.deploymentStatus}
                </Badge>
              </HStack>
            </Flex>
            {r.plannedReleaseDate && (
              <Text fontSize="xs" color="gray.500">
                Planned: {new Date(r.plannedReleaseDate).toLocaleDateString()}
              </Text>
            )}
          </Box>
        ))
      )}
    </VStack>
  );
}
